import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, ShoppingCart, Truck, RotateCcw, Boxes, Users as UsersIcon, Store, CheckCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { notificationsService } from "@/services/notificationsService";
import { formatRelativeTime } from "@/lib/format";

const CATEGORY_META = {
  orders: { icon: ShoppingCart, tone: "text-primary bg-primary/12 ring-primary/20" },
  inbound: { icon: Truck, tone: "text-amber-600 bg-amber-500/12 ring-amber-500/20 dark:text-amber-400" },
  returns: { icon: RotateCcw, tone: "text-emerald-600 bg-emerald-500/12 ring-emerald-500/20 dark:text-emerald-400" },
  inventory: { icon: Boxes, tone: "text-rose-600 bg-rose-500/12 ring-rose-500/20 dark:text-rose-400" },
  users: { icon: UsersIcon, tone: "text-primary bg-primary/12 ring-primary/20" },
  stores: { icon: Store, tone: "text-primary bg-primary/12 ring-primary/20" },
};

export function NotificationMenu() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const countQuery = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: notificationsService.unreadCount,
    refetchInterval: 30000,
  });
  const listQuery = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => notificationsService.list({ limit: 20 }),
    enabled: open,
  });

  const unread = countQuery.data?.unread ?? 0;
  const items = listQuery.data?.items || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });

  const handleClick = async (n) => {
    if (!n.read) {
      await notificationsService.markRead(n.id);
      refresh();
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    await notificationsService.markAllRead();
    refresh();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="notification-trigger"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-transparent text-muted-foreground transition-all hover:border-border/60 hover:bg-background/60 hover:text-foreground focus-ring"
          aria-label="Notifications"
        >
          <Bell className="h-[18px] w-[18px]" />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                key="badge"
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 24 }}
                data-testid="notification-badge"
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow-[0_0_10px_rgb(244_63_94/0.6)]"
              >
                {unread > 9 ? "9+" : unread}
              </motion.span>
            )}
          </AnimatePresence>
          {unread > 0 && (
            <span className="pointer-events-none absolute inset-0 rounded-lg animate-ping bg-primary/10" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass-strong shadow-premium w-96 overflow-hidden rounded-2xl border-border/60 p-0">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div>
            <p className="overline">Inbox</p>
            <span className="font-display mt-1 block text-base font-semibold tracking-tight text-foreground">
              Notifications
            </span>
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={handleMarkAll}
              data-testid="notification-mark-all"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="scrollbar-thin max-h-96 overflow-y-auto py-1">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
              <Bell className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              You're all caught up.
            </div>
          ) : (
            items.map((n) => {
              const meta = CATEGORY_META[n.category] || { icon: Bell, tone: "text-muted-foreground bg-muted ring-border/60" };
              const Icon = meta.icon;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={cn(
                    "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50",
                    !n.read && "bg-primary/5"
                  )}
                >
                  <div className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset", meta.tone)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{n.title}</p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground/80">{formatRelativeTime(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.6)]" />
                  )}
                </button>
              );
            })
          )}
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); navigate("/activity-log"); }}
          data-testid="notification-view-all"
          className="block w-full border-t border-border/60 px-4 py-2.5 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/10"
        >
          View all activity →
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
